/**
 * Turns a brand skin (KP-4497 shape, served by plg-go-be) into CSS custom
 * properties and @font-face rules for the branded line sheet (KP-4545).
 *
 * plg-go-be has already checked the skin, but these values end up inside a
 * style attribute and a <style> element, so every one is re-validated here
 * against a strict shape: a colour must be #rrggbb, a family a plain name, a
 * URL https. Anything else falls back. Contrast is checked again too, so a
 * skin that reaches the page unreadable degrades to the line sheet's own
 * editorial palette instead of grey text on grey.
 */

const HEX = /^#[0-9a-f]{6}$/i
const FAMILY = /^[A-Za-z0-9][A-Za-z0-9 \-_.']{0,63}$/
const GENERIC_FAMILIES = new Set([
  'serif', 'sans-serif', 'monospace', 'cursive', 'system-ui', 'ui-serif', 'ui-sans-serif',
])
const LENGTH = /^-?(\d+(\.\d+)?|\.\d+)(px|rem|em)$/
const WEIGHT = /^[1-9]00$/
const FORMATS = new Set(['woff2', 'woff', 'truetype', 'opentype'])
const SAFE_URL = /^https:\/\/[^\s"'()\\]+$/

const AA = 4.5
const SYSTEM_SANS = 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
// A bare generic serif renders as Times; these book faces ship with every OS.
const BOOK_SERIF = '"Iowan Old Style", "Palatino Linotype", Palatino, "Book Antiqua", Georgia, serif'
const GENERIC_STACKS = { serif: BOOK_SERIF, 'sans-serif': SYSTEM_SANS, 'system-ui': SYSTEM_SANS }

/** The line sheet's own light editorial palette (plg-go-be FallbackSkin). */
export const FALLBACK_TOKENS = Object.freeze({
  ground: '#faf8f5',
  surface: '#ffffff',
  surface_2: '#f1ede6',
  ink: '#16130f',
  muted: '#5c554c',
  line: '#e3ded6',
  primary: '#16130f',
  on_primary: '#faf8f5',
})

const hex = value => (HEX.test(String(value || '')) ? String(value).toLowerCase() : '')

function channel(byte) {
  const c = byte / 255
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
}

function luminance(colour) {
  const n = parseInt(colour.slice(1), 16)
  // eslint-disable-next-line no-bitwise
  const [r, g, b] = [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff]
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

/** WCAG 2 contrast ratio of two #rrggbb colours. */
export function contrastRatio(a, b) {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x)
  return (hi + 0.05) / (lo + 0.05)
}

const reads = (fg, bg) => Boolean(fg && bg) && contrastRatio(fg, bg) >= AA

function cleanTokens(tokens) {
  const t = {}
  Object.keys(tokens || {}).forEach(key => {
    const value = hex(tokens[key])
    if (value) t[key] = value
  })
  return t
}

/** A surface ink text reads on, else the fallback. */
const inkSurface = (surface, ink, fallback) => (reads(ink, surface) ? surface : fallback)

/** The base palette: the skin's when ink reads on its ground, else ours. */
function basePalette(t) {
  if (!reads(t.ink, t.ground)) return { ...FALLBACK_TOKENS }
  const surface = inkSurface(t.surface, t.ink, t.ground)
  return {
    ground: t.ground,
    surface,
    surface_2: inkSurface(t.surface_2, t.ink, surface),
    ink: t.ink,
    muted: reads(t.muted, t.ground) ? t.muted : t.ink,
    line: t.line || t.muted || t.ink,
  }
}

/** The action pair: primary when its text reads on it, else ink on ground. */
function actionPair(t, base) {
  if (reads(t.on_primary, t.primary) && t.primary) {
    return { primary: t.primary, on_primary: t.on_primary }
  }
  return { primary: base.ink, on_primary: base.ground }
}

/** A readable palette with every role the page uses. */
export function readableTokens(tokens) {
  const t = cleanTokens(tokens)
  const base = basePalette(t)
  const action = reads(t.ink, t.ground)
    ? actionPair(t, base)
    : { primary: FALLBACK_TOKENS.primary, on_primary: FALLBACK_TOKENS.on_primary }
  // The accent marks small things (active chip, shortlist heart) on the ground.
  const fallbackAccent = reads(action.primary, base.ground) ? action.primary : base.ink
  const accent = reads(t.accent, base.ground) ? t.accent : fallbackAccent
  return { ...base, ...action, accent }
}

function safeFamily(family) {
  const name = String(family || '').trim()
  return FAMILY.test(name) ? name : ''
}

function safeFallback(fallback) {
  const parts = String(fallback || '').split(',').map(p => p.trim()).filter(Boolean)
  const ok = parts.filter(p => GENERIC_FAMILIES.has(p) || FAMILY.test(p.replace(/^"|"$/g, '')))
  if (!ok.length) return ''
  if (ok.length === 1 && GENERIC_STACKS[ok[0]]) return GENERIC_STACKS[ok[0]]
  return ok.map(p => (GENERIC_FAMILIES.has(p) ? p : `"${p.replace(/^"|"$/g, '')}"`)).join(', ')
}

/** A CSS font-family list for a skin font role. */
export function fontStack(font = {}) {
  const family = safeFamily(font.family)
  const fallback = safeFallback(font.fallback) || SYSTEM_SANS
  return family ? `"${family}", ${fallback}` : fallback
}

/** CSS text-transform for a skin case value. */
export function textTransform(value) {
  switch (String(value || '').toLowerCase()) {
    case 'uppercase':
    case 'upper':
      return 'uppercase'
    case 'lowercase':
    case 'lower':
      return 'lowercase'
    case 'title':
    case 'capitalize':
      return 'capitalize'
    default:
      return 'none'
  }
}

const length = (value, fallback) => (LENGTH.test(String(value || '')) ? String(value) : fallback)
const weight = (value, fallback) => (WEIGHT.test(String(value || '')) ? String(value) : fallback)

/** CSS custom properties for the page root. */
export function skinCssVars(skin = {}) {
  const t = readableTokens(skin.tokens)
  const heading = skin.heading_font || {}
  const body = skin.body_font || {}
  return {
    '--ls-ground': t.ground,
    '--ls-surface': t.surface,
    '--ls-surface-2': t.surface_2,
    '--ls-ink': t.ink,
    '--ls-muted': t.muted,
    '--ls-line': t.line,
    '--ls-primary': t.primary,
    '--ls-on-primary': t.on_primary,
    '--ls-accent': t.accent,
    '--ls-radius': length(skin.radius, '0px'),
    '--ls-heading-font': fontStack(heading),
    '--ls-heading-weight': weight(heading.weight, '400'),
    '--ls-heading-case': textTransform(heading.case),
    '--ls-heading-tracking': length(heading.tracking, '0em'),
    '--ls-body-font': fontStack(body),
    '--ls-body-weight': weight(body.weight, '400'),
    '--ls-body-tracking': length(body.tracking, '0em'),
  }
}

function faceRule(family, face) {
  const url = String(face.url || '')
  const format = String(face.format || '').toLowerCase()
  if (!SAFE_URL.test(url) || !FORMATS.has(format)) return ''
  const style = face.style === 'italic' ? 'italic' : 'normal'
  return `@font-face { font-family: "${family}"; src: url("${url}") format("${format}"); `
    + `font-weight: ${weight(face.weight, '400')}; font-style: ${style}; font-display: swap; }`
}

/** @font-face rules for the stored faces of both roles. */
export function fontFaceCss(skin = {}) {
  return [skin.heading_font, skin.body_font]
    .filter(Boolean)
    .flatMap(font => {
      const family = safeFamily(font.family)
      if (!family) return []
      return (Array.isArray(font.src) ? font.src : []).map(face => faceRule(family, face || {}))
    })
    .filter(Boolean)
    .join('\n')
}
